import { html } from "htm/preact";
import { type RefObject } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { APIClient } from "../modules/api";
import * as Bible from "../modules/bible";
import { dispatchEvent } from "../modules/events";

import { BibleContent, BibleContentSettings } from "./BibleContent";

export type BibleReaderProps = {
  contentRef: RefObject<Bible.Content>;
  query: string;
  settings: Partial<BibleContentSettings>;
  translation: number;
};

export const BibleReader = (props: BibleReaderProps) => {
  const [content, setContent] = useState<Bible.Content | null>(
    // Allow content to be passed in directly.
    // In the case of SSR, <bible-reader> will fetch the content (while the SSR'd content is displayed)
    // and then hydrate the component tree once ready
    props.contentRef.current,
  );
  const [translations, setTranslations] = useState(
    () => new Map<number, Bible.TranslationPayload>(),
  );
  const bibleQueryController = useRef<AbortController | null>(null);
  useEffect(() => {
    APIClient.shared.bibleTranslations().then((translationsPayload) => {
      setTranslations(
        translationsPayload.reduce((lookup, payload) => {
          lookup.set(payload.id, payload);
          return lookup;
        }, new Map()),
      );
    });
  }, []);
  useEffect(() => {
    if (
      content?.query.toLowerCase() === props.query.toLowerCase() &&
      content?.translation === props.translation
    ) {
      dispatchEvent("loaded", {
        query: props.query,
        translation: props.translation,
      });
      // Prevent requests if the content was already loaded
      return;
    }
    /* c8 ignore start */
    bibleQueryController.current?.abort();
    /* c8 ignore end */
    bibleQueryController.current = new AbortController();
    APIClient.shared
      .bibleQuery(props.query, props.translation, bibleQueryController.current)
      .then((res) => {
        setContent(new Bible.Content(res));
        setTimeout(() => {
          dispatchEvent("loaded", {
            query: props.query,
            translation: props.translation,
          });
        });
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === "AbortError") {
          return;
        }
        dispatchEvent("loaderror", {
          query: props.query,
          translation: props.translation,
        });
      });
  }, [props.query, props.translation]);
  useEffect(() => {
    props.contentRef.current = content;
  }, [content]);
  return html`
    <div class="BibleReader">
      ${content
        ? html`<${BibleContent}
            content=${content}
            settings=${props.settings}
            translation=${translations.get(content.translation)}
          />`
        : null}
    </div>
  `;
};
